import { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '@/contexts/AdminContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { FileText, Save, ArrowLeft, RefreshCw, Eye } from 'lucide-react';
import DOMPurify from 'dompurify';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAGES = [
  { slug: 'privacy-policy', label: 'Privacy Policy', route: '/privacy' },
  { slug: 'terms-conditions', label: 'Terms & Conditions', route: '/terms' },
  { slug: 'refund-cancellation', label: 'Refund & Cancellation', route: '/cancellation' },
];

const SitePages = () => {
  const { getAuthHeaders } = useAdmin();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchPages = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/pages`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      setPages(data);
    } catch {
      toast.error('Failed to load pages');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const startEdit = (page) => {
    setEditing(page.slug);
    setEditTitle(page.title);
    setEditContent(page.content);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/pages/${editing}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: editTitle, content: editContent }),
      });
      if (res.ok) {
        toast.success('Page updated successfully');
        setEditing(null);
        fetchPages();
      } else {
        toast.error('Failed to update page');
      }
    } catch {
      toast.error('Failed to update page');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (editing) {
    const meta = PAGES.find((p) => p.slug === editing);
    return (
      <div className="space-y-6" data-testid="page-editor">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setEditing(null)}
            className="text-slate-500 hover:text-blue-600 transition-colors"
            data-testid="editor-back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-800">
              Edit: {meta?.label || editing}
            </h1>
            <p className="text-sm text-slate-500">
              Edit the HTML content below. Changes are saved to database and reflected on the public page.
            </p>
          </div>
          <a
            href={meta?.route}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-blue-600 hover:underline flex items-center gap-1"
          >
            <Eye size={14} /> Preview
          </a>
        </div>

        <Card>
          <CardContent className="p-5 space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Page Title</label>
              <Input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                data-testid="page-title-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Content (HTML)
              </label>
              <Textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                rows={24}
                className="font-mono text-xs leading-relaxed"
                data-testid="page-content-editor"
              />
            </div>
            <div className="flex items-center justify-between pt-2">
              <Button variant="outline" onClick={() => setEditing(null)} data-testid="cancel-edit-btn">
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="save-page-btn"
              >
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Live Preview */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Live Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className="prose prose-slate prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(editContent) }}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="site-pages-admin">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Site Pages</h1>
        <p className="text-slate-500">Manage your legal and information pages</p>
      </div>

      <div className="grid gap-4">
        {PAGES.map((meta) => {
          const page = pages.find((p) => p.slug === meta.slug);
          return (
            <Card
              key={meta.slug}
              className="hover:shadow-md transition-shadow cursor-pointer"
              data-testid={`page-card-${meta.slug}`}
            >
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-800">{meta.label}</h3>
                  <p className="text-xs text-slate-400">
                    {page
                      ? `Last updated: ${new Date(page.updated_at).toLocaleDateString()}`
                      : 'Not yet created'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={meta.route}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Eye size={14} /> View
                  </a>
                  <Button
                    size="sm"
                    onClick={() => page && startEdit(page)}
                    disabled={!page}
                    data-testid={`edit-${meta.slug}-btn`}
                  >
                    Edit
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default SitePages;
